import { BlurTargetView, BlurView, type BlurTint } from 'expo-blur';
import { VideoView, useVideoPlayer, type VideoSource } from 'expo-video';
import * as React from 'react';
import { Pressable, StyleSheet, View, type ColorValue, type ViewProps } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Text } from '@sixam/ui/text';

import { BRAND } from '@sixam/config/brand';
import { GLASS } from '@sixam/config/glass';
import { cn } from '@sixam/ui/utils';

type BrandBackdropProps = ViewProps &
  Readonly<{
    source: VideoSource;
    children: React.ReactNode;
    initialBlurIntensity?: number;
    blurTint?: BlurTint;
    scrimColor?: ColorValue;
    scrimOpacity?: number;
    showBlurDial?: boolean;
  }>;

const overlayBlendStyle = {
  backgroundColor: 'rgba(255, 85, 0, 0.16)',
  mixBlendMode: 'overlay',
} as const;

const BLUR_STEP = 4;
const MIN_BLUR = 64;
const MAX_BLUR = 100;
const MAX_SCRIM_OPACITY = 0.74;
const ANDROID_BLUR_REDUCTION_FACTOR = 2;

function BackdropVideo({ source }: Readonly<{ source: VideoSource }>) {
  const player = useVideoPlayer(source, (videoPlayer) => {
    videoPlayer.loop = true;
    videoPlayer.muted = true;
    videoPlayer.allowsExternalPlayback = false;
    videoPlayer.keepScreenOnWhilePlaying = false;
    videoPlayer.audioMixingMode = 'mixWithOthers';
    videoPlayer.play();
  });

  return (
    <VideoView
      player={player}
      style={[StyleSheet.absoluteFill, { height: '100%', width: '100%' }]}
      contentFit="cover"
      fullscreenOptions={{ enable: false }}
      nativeControls={false}
      playsInline
      surfaceType="textureView"
      useExoShutter={false}
    />
  );
}

function ControlDial({
  label,
  value,
  decreaseLabel,
  increaseLabel,
  onChange,
  onDecrease,
  onIncrease,
}: Readonly<{
  label: string;
  value: number;
  decreaseLabel: string;
  increaseLabel: string;
  onChange: (nextValue: number) => void;
  onDecrease: (value: number) => number;
  onIncrease: (value: number) => number;
}>) {
  const decrease = React.useCallback(() => {
    onChange(onDecrease(value));
  }, [onChange, onDecrease, value]);

  const increase = React.useCallback(() => {
    onChange(onIncrease(value));
  }, [onChange, onIncrease, value]);

  return (
    <View className="overflow-hidden rounded-full border border-white/35 bg-black/70">
      <View className="flex-row items-center gap-1 px-2 py-2">
        <Pressable
          accessibilityLabel={decreaseLabel}
          className="h-10 w-10 items-center justify-center rounded-full bg-white/15 active:bg-white/25"
          onPress={decrease}
          role="button"
        >
          <Text className="font-mono text-xl font-extrabold leading-none text-white">-</Text>
        </Pressable>
        <View className="min-w-[66px] items-center px-1">
          <Text className="font-mono text-[10px] uppercase leading-none text-white/60">
            {label}
          </Text>
          <Text className="mt-1 font-mono text-lg font-extrabold leading-none text-white">
            {value.toString().padStart(2, '0')}
          </Text>
        </View>
        <Pressable
          accessibilityLabel={increaseLabel}
          className="h-10 w-10 items-center justify-center rounded-full bg-white/15 active:bg-white/25"
          onPress={increase}
          role="button"
        >
          <Text className="font-mono text-xl font-extrabold leading-none text-white">+</Text>
        </Pressable>
      </View>
    </View>
  );
}

function BlurDial({
  value,
  onChange,
}: Readonly<{
  value: number;
  onChange: (nextValue: number) => void;
}>) {
  return (
    <ControlDial
      label="Blur"
      value={value}
      decreaseLabel="Decrease background blur"
      increaseLabel="Increase background blur"
      onChange={onChange}
      onDecrease={(currentValue) => Math.max(MIN_BLUR, currentValue - BLUR_STEP)}
      onIncrease={(currentValue) => Math.min(MAX_BLUR, currentValue + BLUR_STEP)}
    />
  );
}

function BrandBackdrop({
  source,
  children,
  initialBlurIntensity = GLASS.blur.backdrop,
  blurTint = 'dark',
  className,
  scrimColor,
  scrimOpacity = MAX_SCRIM_OPACITY,
  showBlurDial = false,
  style,
  ...props
}: BrandBackdropProps) {
  const [blurIntensity, setBlurIntensity] = React.useState<number>(initialBlurIntensity);
  const insets = useSafeAreaInsets();
  const blurTargetRef = React.useRef<View | null>(null);
  const resolvedScrimColor = scrimColor ?? `rgba(0, 0, 0, ${scrimOpacity.toFixed(2)})`;
  const controlLeft = Math.max(insets.left + 12, 12);

  return (
    <View
      className={cn('relative flex-1 overflow-hidden', className)}
      style={[{ backgroundColor: BRAND.color.ink }, style]}
      {...props}
    >
      <BlurTargetView ref={blurTargetRef} style={StyleSheet.absoluteFill}>
        <BackdropVideo key={String(source)} source={source} />
      </BlurTargetView>
      <BlurView
        blurMethod="dimezisBlurView"
        blurReductionFactor={ANDROID_BLUR_REDUCTION_FACTOR}
        blurTarget={blurTargetRef}
        intensity={blurIntensity}
        style={StyleSheet.absoluteFill}
        tint={blurTint}
      />
      <View
        style={[
          StyleSheet.absoluteFill,
          {
            backgroundColor: resolvedScrimColor,
            pointerEvents: 'none',
          },
        ]}
      />
      <View style={[StyleSheet.absoluteFill, overlayBlendStyle, { pointerEvents: 'none' }]} />
      <View
        style={[
          StyleSheet.absoluteFill,
          {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
          },
        ]}
      />
      <View className="relative flex-1">{children}</View>
      {showBlurDial ? (
        <View
          className="absolute items-start gap-2"
          pointerEvents="box-none"
          style={{
            elevation: 100,
            left: controlLeft,
            top: '50%',
            transform: [{ translateY: -32 }],
            zIndex: 10000,
          }}
        >
          <BlurDial value={blurIntensity} onChange={setBlurIntensity} />
        </View>
      ) : null}
    </View>
  );
}

export { BrandBackdrop };
